import type { Traducao } from '../tipos';

/**
 * Textos de `apreciacaoEdicao` — o editor da apreciação. Um ramo por arquivo:
 * `dadosMaquina` (etapa 1 ArcelorMittal), `praxis` (etapa 1 modelo Praxis),
 * `informacoes` (informações gerais da máquina) e `cabecalho` (identificação
 * documental). Ver ../README.md.
 */
export const apreciacaoEdicao: Traducao = {
  pt: {
    dadosMaquina: {
      imagem: {
        erroCarregar: 'Erro ao carregar imagem',
      },
      upload: {
        salvePrimeiro: 'Salve a apreciação primeiro para fazer upload de imagens',
        erroPanoramica: 'Erro ao fazer upload da foto panorâmica',
        erroIdentificacao: 'Erro ao fazer upload da foto de identificação',
        arrasteVazio: 'Nenhum arquivo encontrado no arraste.',
        arquivoInvalido: 'Arquivo inválido. Arraste uma imagem (JPG, PNG, HEIC, etc.).',
      },
      assinatura: {
        erroSalvar: 'Erro ao salvar assinatura. Tente novamente.',
      },
      copiar: {
        botao: 'Copiar dados de outra máquina',
        titulo: 'Copiar todos os dados desta etapa a partir de outra apreciação',
        confirmacao: 'Isso substituirá os dados desta etapa pelos dados da máquina selecionada. Deseja continuar?',
      },
      revisao: {
        titulo: 'REVISÃO',
        numeroArcelorMittal: 'Nº ArcelorMittal',
        demanda: 'DEMANDA',
        data: 'DATA',
      },
      usuarioPadrao: 'Usuário padrão:',
      usarUsuario: 'Usar {{nome}}',
      executante: {
        titulo: 'Info Executante (Elaborador)',
        selecaoLabel: 'Executante (Elaborador)',
        rotuloNome: 'Executante',
        dataElaboracao: 'Data de Elaboração',
      },
      verificador: {
        titulo: 'Info Verificador',
        rotulo: 'Verificador',
        dataVerificacao: 'Data de Verificação',
      },
      responsavelTecnico: {
        titulo: 'Info Responsável Técnico',
        rotulo: 'Responsável Técnico',
        dataAprovacao: 'Data de Aprovação',
      },
      pessoa: {
        nome: 'Nome:',
        crea: 'CREA:',
        cargo: 'Cargo:',
        dataVerificacao: 'Data de Verificação:',
        dataAprovacao: 'Data de Aprovação:',
      },
      fotos: {
        titulo: 'Informações da máquina',
        panoramica: 'Foto panorâmica',
        identificacao: 'Foto de identificação',
        arrasteSolteOu: 'Arraste e solte ou',
        cliqueSelecionar: 'clique para selecionar',
        enviando: 'Enviando...',
        altPanoramica: 'Panorâmica',
        altIdentificacao: 'Identificação',
      },
      identificacao: {
        salvePrimeiro: 'Salve a apreciação primeiro para gerar a foto de identificação.',
        erroGerar: 'Erro ao gerar foto de identificação.',
        preenchaUmCampo: 'Preencha pelo menos um campo.',
        tituloBotao: 'Gerar placa de identificação com nome da máquina, SISMANA e logo ArcelorMittal',
        gerando: 'Gerando...',
        criarBotao: 'Criar\nIdentificação',
        tituloForm: 'Dados da placa de identificação',
        labelDescricao: 'Nome / Descrição da máquina',
        placeholderDescricao: 'Ex: Sistema de recuperação de areia',
        labelSismana: 'Número SISMANA / TAG',
        placeholderSismana: 'Ex: 20.03.028',
        gerarImagem: 'Gerar Imagem',
        gerandoImagem: 'Gerando imagem...',
      },
      c1: {
        titulo: 'C.1. Informações da máquina',
        tag: 'C.1.1. TAG da máquina',
        localizacao: 'C.1.2. Localização',
        fabricante: 'C.1.3. Nome do fabricante',
        modelo: 'C.1.4. Modelo da máquina',
        serie: 'C.1.5. Número de série',
        ano: 'C.1.6. Ano de fabricação',
        descricao: 'C.1.7. Descrição da máquina',
        tensaoAlimentacao: 'C.1.8. Tensão de alimentação',
        placeholderTensaoAlimentacao: 'Ex: 440 Vca',
        tensaoComando: 'C.1.9. Tensão de comando',
        placeholderTensaoComando: 'Ex: 220 Vca',
      },
      c2: {
        titulo: 'C.2. Responsável pela máquina',
        nome: 'C.2.1. Nome',
        funcao: 'C.2.2. Função',
        area: 'C.2.3. Área',
        dataEntrevista: 'C.2.4. Data da entrevista',
        empresa: 'C.2.5. Empresa',
      },
      c3: {
        titulo: 'C.3. Limites da máquina',
        uso: 'C.3.1. Limites de uso',
        espaco: 'C.3.2. Limites de espaço',
        tempo: 'C.3.3. Limites de tempo',
        historicoAcidentes: 'C.3.4. Histórico de acidentes',
      },
    },
    praxis: {
      titulo: 'Modelo Praxis - Documento',
      pendencias: {
        titulo: 'Dados pendentes para fechar o Praxis',
        restantes: '+{{quantidade}} pendência(s)',
      },
      entradas: {
        titulo: 'Entradas variáveis do modelo Praxis',
      },
      documento: {
        numero: 'Nº Praxis / Documento',
        clienteFinal: 'Cliente final',
        dataEmissao: 'Data de emissão',
        descricaoServico: 'Descrição do serviço',
      },
      contatos: {
        titulo: 'Contatos e Referências',
        cliente: 'Pessoa de contato no cliente',
        schmersal: 'Pessoas de contato na SCHMERSAL',
        funcao: 'Função',
        dadosContato: 'Dados de contato',
        removerTitulo: 'Remover contato',
      },
      normas: {
        nacionais: 'Normas nacionais complementares',
        internacionais: 'Normas internacionais complementares',
      },
      maquina: {
        titulo: 'Identificação da Máquina',
        nome: 'Máquina',
        identificacao: 'Identificação',
        setor: 'Setor',
        revisao: 'Revisão',
        funcao: 'Função da máquina',
      },
      fotos: {
        titulo: 'Fotos da Máquina',
        urlPanoramica: 'URL da foto panorâmica',
        urlIdentificacao: 'URL da foto de identificação',
        aviso: 'Upload com arrastar/soltar será conectado na próxima etapa; por enquanto, as URLs salvas já entram no PDF Praxis.',
      },
      usuarioPadrao: 'Usuário padrão:',
      usarUsuario: 'Usar {{nome}}',
      assinatura: {
        titulo: 'Criado por / Assinatura',
        consultorTecnico: 'Consultor técnico',
      },
      limites: {
        titulo: 'Limites da Máquina - Praxis',
        tituloColuna: 'Título',
        textoLivre: 'Texto livre',
        espaco: 'Limites de Espaço',
        adicionais: 'Limites Adicionais',
      },
      checklist: {
        titulo: 'Checklist Técnico/Administrativo Praxis',
        colunaItem: 'Item',
        placeholderObservacao: 'Observações...',
      },
      conclusao: {
        titulo: 'Conclusão e Assinatura',
        cidade: 'Cidade da assinatura',
        dataAprovacao: 'Data de aprovação',
        conclusoesFinais: 'Conclusões finais',
        exclusaoResponsabilidade: 'Exclusão de responsabilidade',
      },
    },
    informacoes: {
      titulo: 'Informações Gerais da Máquina',
      imagem: {
        erroCarregar: 'Erro ao carregar imagem',
      },
      identificacao: {
        titulo: 'Identificação',
        site: 'Site',
        areaSetor: 'Área/Setor',
        fabricante: 'Fabricante',
        modelo: 'Modelo',
        funcaoMaquina: 'Função da Máquina',
        placeholderFuncao: 'Descreva a função da máquina...',
      },
      entrevista: {
        titulo: 'Entrevistados e Dados de Uso',
        operador: 'Operador Entrevistado',
        cargo: 'Cargo',
        experiencia: 'Experiência (anos)',
        experienciaValor: '{{valor}} anos',
        turnos: 'Turnos de Trabalho',
        placeholderTurnos: 'Ex: 1º, 2º, 3º',
        frequenciaUso: 'Frequência de Uso',
        placeholderFrequenciaUso: 'Ex: Diário, Semanal',
        frequenciaOperacao: 'Frequência de Acesso pela Operação',
        placeholderFrequenciaOperacao: 'Ex: 1 vez por dia',
        frequenciaManutencao: 'Frequência de Acesso pela Manutenção',
        placeholderFrequenciaManutencao: 'Ex: 1 vez por semana',
        dataEntrevista: 'Data da Entrevista',
        modoOperacao: 'Modo de Operação',
        historicoAcidentes: 'Histórico de Acidentes',
      },
      tecnicas: {
        titulo: 'Informações Técnicas da Máquina',
        comprimentoHorizontal: 'Comprimento Horizontal',
        placeholderComprimentoHorizontal: 'Ex: 215m',
        elevacao: 'Elevação',
        capacidadeCorreia: 'Capacidade Correia',
        placeholderCapacidadeCorreia: 'Ex: 16000/13350 (t/h)',
        larguraCorreia: 'Largura da Correia',
        placeholderLarguraCorreia: 'Ex: 1800 mm',
        velocidadeCorreia: 'Velocidade da Correia',
        placeholderVelocidadeCorreia: 'Ex: 4,8 (m/s)',
        material: 'Material',
        contraRecuo: 'Contra Recuo',
        freio: 'Freio',
        potencia: 'Potência',
        placeholderPotencia: 'Ex: 600cv',
        tensaoAcionamento: 'Tensão de Acionamento',
        tensaoComando: 'Tensão de Comando',
        placeholderTensao: 'Ex: 440 vca',
        placeholderNaoInformado: 'Ex: N/I',
      },
      limites: {
        titulo: 'Limites da Máquina',
        uso: 'Limites de Uso',
        espaco: 'Limites de Espaço',
        adicionais: 'Limites Adicionais',
        ambientais: 'Limites Ambientais',
        espacosTitulo: 'Espaços Disponíveis',
        espacoPecasFerramentas: 'Existe espaço para peças e ferramentas?',
        espacoOperacaoNormal: 'Existe espaço para operação normal da máquina?',
        espacoManutencao: 'Existe espaço para manutenção da máquina?',
        espacoAjuste: 'Existe espaço para ajuste da máquina?',
        espacoLimpeza: 'Existe espaço para limpeza da máquina?',
      },
      imagens: {
        titulo: 'Imagens da Máquina (Seção E)',
        panoramica: 'Foto Panorâmica',
        identificacao: 'Foto Identificação',
        altPanoramica: 'Panorâmica',
        altIdentificacao: 'Identificação',
        erroProcessar: 'Erro ao processar a imagem. Tente converter para JPG ou PNG antes de fazer upload.',
      },
      mauUso: {
        titulo: 'Mau Uso Razoavelmente Previsível',
        placeholder: 'Adicionar mau uso previsível',
        vazio: 'Nenhum item cadastrado',
      },
      normas: {
        titulo: 'Normas Técnicas de Referência',
        placeholder: 'Ex: ABNT NBR ISO 12100',
        vazio: 'Nenhuma norma cadastrada',
      },
      extras: {
        titulo: 'Informações adicionais da máquina',
      },
      notas: {
        nota1: 'Nota 1: Limites de Análise Estrutural',
        nota2: 'Nota 2: Atmosferas Explosivas',
      },
    },
    cabecalho: {
      titulo: 'Cabeçalho / Identificação Documental',
      tituloPadrao: 'NR-12 – Apreciação de Riscos',
      status: {
        liberado: 'Liberado',
        comRessalvas: 'Com Ressalvas',
        naoLiberado: 'Não Liberado',
        naoDefinido: 'Não Definido',
      },
      ativo: {
        label: 'Ativo (Máquina)',
        semNome: 'Sem nome',
        placeholder: 'Clique para selecionar um ativo...',
        removerSelecao: 'Remover seleção',
        naoEspecificado: 'Não especificado',
      },
      codigoPraxis: 'Código Praxis',
      placeholderCodigoPraxis: 'Ex: 2026/03-0001',
      numeroArcelorMittal: 'Nº ArcelorMittal',
      placeholderNumeroArcelorMittal: 'Ex: ARCM-2024-001',
      revisao: 'Revisão',
      numeroEmitente: 'Nº Emitente',
      placeholderNumeroEmitente: 'Nº do emitente',
      placeholderDescricao: 'Descrição da apreciação',
      demanda: 'Demanda',
      placeholderDemanda: 'Ex: 19.06.114',
      numeroDocumento: 'Nº Documento (Rodapé)',
      placeholderNumeroDocumento: 'Ex: 2024/26-0053',
      emitente: 'Emitente',
      placeholderEmitente: 'Nome do emitente',
      maquina: 'Máquina',
      placeholderMaquina: 'Nome da máquina',
      placeholderTipoMaquina: 'Tipo da máquina',
      identificacao: 'Identificação',
      placeholderIdentificacao: 'Identificação da máquina',
      placeholderLocal: 'Local da máquina',
      statusLiberacao: 'Status de Liberação',
    },
  },
  en: {
    dadosMaquina: {
      imagem: {
        erroCarregar: 'Could not load image',
      },
      upload: {
        salvePrimeiro: 'Save the risk assessment first to upload images',
        erroPanoramica: 'Could not upload the panoramic photo',
        erroIdentificacao: 'Could not upload the identification photo',
        arrasteVazio: 'No file found in the drop.',
        arquivoInvalido: 'Invalid file. Drop an image (JPG, PNG, HEIC, etc.).',
      },
      assinatura: {
        erroSalvar: 'Could not save the signature. Try again.',
      },
      copiar: {
        botao: 'Copy data from another machine',
        titulo: 'Copy every field of this step from another risk assessment',
        confirmacao: 'This will replace the data in this step with the data from the selected machine. Continue?',
      },
      revisao: {
        titulo: 'REVISION',
        numeroArcelorMittal: 'ArcelorMittal No.',
        demanda: 'REQUEST',
        data: 'DATE',
      },
      usuarioPadrao: 'Default user:',
      usarUsuario: 'Use {{nome}}',
      executante: {
        titulo: 'Executor (Author) Info',
        selecaoLabel: 'Executor (Author)',
        rotuloNome: 'Executor',
        dataElaboracao: 'Authoring Date',
      },
      verificador: {
        titulo: 'Verifier Info',
        rotulo: 'Verifier',
        dataVerificacao: 'Verification Date',
      },
      responsavelTecnico: {
        titulo: 'Technical Manager Info',
        rotulo: 'Technical Manager',
        dataAprovacao: 'Approval Date',
      },
      pessoa: {
        nome: 'Name:',
        crea: 'CREA:',
        cargo: 'Role:',
        dataVerificacao: 'Verification Date:',
        dataAprovacao: 'Approval Date:',
      },
      fotos: {
        titulo: 'Machine information',
        panoramica: 'Panoramic photo',
        identificacao: 'Identification photo',
        arrasteSolteOu: 'Drag and drop or',
        cliqueSelecionar: 'click to select',
        enviando: 'Uploading...',
        altPanoramica: 'Panoramic',
        altIdentificacao: 'Identification',
      },
      identificacao: {
        salvePrimeiro: 'Save the risk assessment first to generate the identification photo.',
        erroGerar: 'Could not generate the identification photo.',
        preenchaUmCampo: 'Fill in at least one field.',
        tituloBotao: 'Generate the identification plate with machine name, SISMANA and ArcelorMittal logo',
        gerando: 'Generating...',
        criarBotao: 'Create\nIdentification',
        tituloForm: 'Identification plate data',
        labelDescricao: 'Machine name / description',
        placeholderDescricao: 'E.g.: Sand reclamation system',
        labelSismana: 'SISMANA / TAG number',
        placeholderSismana: 'E.g.: 20.03.028',
        gerarImagem: 'Generate Image',
        gerandoImagem: 'Generating image...',
      },
      c1: {
        titulo: 'C.1. Machine information',
        tag: 'C.1.1. Machine TAG',
        localizacao: 'C.1.2. Location',
        fabricante: 'C.1.3. Manufacturer name',
        modelo: 'C.1.4. Machine model',
        serie: 'C.1.5. Serial number',
        ano: 'C.1.6. Year of manufacture',
        descricao: 'C.1.7. Machine description',
        tensaoAlimentacao: 'C.1.8. Supply voltage',
        placeholderTensaoAlimentacao: 'E.g.: 440 Vca',
        tensaoComando: 'C.1.9. Control voltage',
        placeholderTensaoComando: 'E.g.: 220 Vca',
      },
      c2: {
        titulo: 'C.2. Machine owner',
        nome: 'C.2.1. Name',
        funcao: 'C.2.2. Role',
        area: 'C.2.3. Area',
        dataEntrevista: 'C.2.4. Interview date',
        empresa: 'C.2.5. Company',
      },
      c3: {
        titulo: 'C.3. Machine limits',
        uso: 'C.3.1. Use limits',
        espaco: 'C.3.2. Space limits',
        tempo: 'C.3.3. Time limits',
        historicoAcidentes: 'C.3.4. Accident history',
      },
    },
    praxis: {
      titulo: 'Praxis Template - Document',
      pendencias: {
        titulo: 'Data still missing to close the Praxis',
        restantes: '+{{quantidade}} pending item(s)',
      },
      entradas: {
        titulo: 'Variable inputs of the Praxis template',
      },
      documento: {
        numero: 'Praxis / Document No.',
        clienteFinal: 'End customer',
        dataEmissao: 'Issue date',
        descricaoServico: 'Service description',
      },
      contatos: {
        titulo: 'Contacts and References',
        cliente: 'Contact person at the customer',
        schmersal: 'Contact people at SCHMERSAL',
        funcao: 'Role',
        dadosContato: 'Contact details',
        removerTitulo: 'Remove contact',
      },
      normas: {
        nacionais: 'Additional national standards',
        internacionais: 'Additional international standards',
      },
      maquina: {
        titulo: 'Machine Identification',
        nome: 'Machine',
        identificacao: 'Identification',
        setor: 'Department',
        revisao: 'Revision',
        funcao: 'Machine function',
      },
      fotos: {
        titulo: 'Machine Photos',
        urlPanoramica: 'Panoramic photo URL',
        urlIdentificacao: 'Identification photo URL',
        aviso: 'Drag-and-drop upload will be connected in the next stage; for now, the saved URLs already go into the Praxis PDF.',
      },
      usuarioPadrao: 'Default user:',
      usarUsuario: 'Use {{nome}}',
      assinatura: {
        titulo: 'Created by / Signature',
        consultorTecnico: 'Technical consultant',
      },
      limites: {
        titulo: 'Machine Limits - Praxis',
        tituloColuna: 'Title',
        textoLivre: 'Free text',
        espaco: 'Space Limits',
        adicionais: 'Additional Limits',
      },
      checklist: {
        titulo: 'Praxis Technical/Administrative Checklist',
        colunaItem: 'Item',
        placeholderObservacao: 'Notes...',
      },
      conclusao: {
        titulo: 'Conclusion and Signature',
        cidade: 'City of signature',
        dataAprovacao: 'Approval date',
        conclusoesFinais: 'Final conclusions',
        exclusaoResponsabilidade: 'Disclaimer',
      },
    },
    informacoes: {
      titulo: 'General Machine Information',
      imagem: {
        erroCarregar: 'Could not load image',
      },
      identificacao: {
        titulo: 'Identification',
        site: 'Site',
        areaSetor: 'Area/Department',
        fabricante: 'Manufacturer',
        modelo: 'Model',
        funcaoMaquina: 'Machine Function',
        placeholderFuncao: 'Describe the machine function...',
      },
      entrevista: {
        titulo: 'Interviewees and Usage Data',
        operador: 'Operator Interviewed',
        cargo: 'Role',
        experiencia: 'Experience (years)',
        experienciaValor: '{{valor}} years',
        turnos: 'Work Shifts',
        placeholderTurnos: 'E.g.: 1st, 2nd, 3rd',
        frequenciaUso: 'Frequency of Use',
        placeholderFrequenciaUso: 'E.g.: Daily, Weekly',
        frequenciaOperacao: 'Access Frequency by Operations',
        placeholderFrequenciaOperacao: 'E.g.: once a day',
        frequenciaManutencao: 'Access Frequency by Maintenance',
        placeholderFrequenciaManutencao: 'E.g.: once a week',
        dataEntrevista: 'Interview Date',
        modoOperacao: 'Operating Mode',
        historicoAcidentes: 'Accident History',
      },
      tecnicas: {
        titulo: 'Machine Technical Information',
        comprimentoHorizontal: 'Horizontal Length',
        placeholderComprimentoHorizontal: 'E.g.: 215m',
        elevacao: 'Elevation',
        capacidadeCorreia: 'Belt Capacity',
        placeholderCapacidadeCorreia: 'E.g.: 16000/13350 (t/h)',
        larguraCorreia: 'Belt Width',
        placeholderLarguraCorreia: 'E.g.: 1800 mm',
        velocidadeCorreia: 'Belt Speed',
        placeholderVelocidadeCorreia: 'E.g.: 4.8 (m/s)',
        material: 'Material',
        contraRecuo: 'Backstop',
        freio: 'Brake',
        potencia: 'Power',
        placeholderPotencia: 'E.g.: 600cv',
        tensaoAcionamento: 'Drive Voltage',
        tensaoComando: 'Control Voltage',
        placeholderTensao: 'E.g.: 440 vca',
        placeholderNaoInformado: 'E.g.: N/I',
      },
      limites: {
        titulo: 'Machine Limits',
        uso: 'Use Limits',
        espaco: 'Space Limits',
        adicionais: 'Additional Limits',
        ambientais: 'Environmental Limits',
        espacosTitulo: 'Available Space',
        espacoPecasFerramentas: 'Is there space for parts and tools?',
        espacoOperacaoNormal: 'Is there space for normal machine operation?',
        espacoManutencao: 'Is there space for machine maintenance?',
        espacoAjuste: 'Is there space for machine adjustment?',
        espacoLimpeza: 'Is there space for machine cleaning?',
      },
      imagens: {
        titulo: 'Machine Images (Section E)',
        panoramica: 'Panoramic Photo',
        identificacao: 'Identification Photo',
        altPanoramica: 'Panoramic',
        altIdentificacao: 'Identification',
        erroProcessar: 'Could not process the image. Try converting it to JPG or PNG before uploading.',
      },
      mauUso: {
        titulo: 'Reasonably Foreseeable Misuse',
        placeholder: 'Add foreseeable misuse',
        vazio: 'No item registered',
      },
      normas: {
        titulo: 'Reference Technical Standards',
        placeholder: 'E.g.: ABNT NBR ISO 12100',
        vazio: 'No standard registered',
      },
      extras: {
        titulo: 'Additional machine information',
      },
      notas: {
        nota1: 'Note 1: Structural Analysis Limits',
        nota2: 'Note 2: Explosive Atmospheres',
      },
    },
    cabecalho: {
      titulo: 'Header / Document Identification',
      tituloPadrao: 'NR-12 – Risk Assessment',
      status: {
        liberado: 'Released',
        comRessalvas: 'Released With Remarks',
        naoLiberado: 'Not Released',
        naoDefinido: 'Not Defined',
      },
      ativo: {
        label: 'Asset (Machine)',
        semNome: 'No name',
        placeholder: 'Click to select an asset...',
        removerSelecao: 'Clear selection',
        naoEspecificado: 'Not specified',
      },
      codigoPraxis: 'Praxis Code',
      placeholderCodigoPraxis: 'E.g.: 2026/03-0001',
      numeroArcelorMittal: 'ArcelorMittal No.',
      placeholderNumeroArcelorMittal: 'E.g.: ARCM-2024-001',
      revisao: 'Revision',
      numeroEmitente: 'Issuer No.',
      placeholderNumeroEmitente: 'Issuer number',
      placeholderDescricao: 'Risk assessment description',
      demanda: 'Request',
      placeholderDemanda: 'E.g.: 19.06.114',
      numeroDocumento: 'Document No. (Footer)',
      placeholderNumeroDocumento: 'E.g.: 2024/26-0053',
      emitente: 'Issuer',
      placeholderEmitente: 'Issuer name',
      maquina: 'Machine',
      placeholderMaquina: 'Machine name',
      placeholderTipoMaquina: 'Machine type',
      identificacao: 'Identification',
      placeholderIdentificacao: 'Machine identification',
      placeholderLocal: 'Machine location',
      statusLiberacao: 'Release Status',
    },
  },
};
